import { Button, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import ModalV2 from '/@/components/bayer/modal-v2';
import Spinner from '/@/components/spinner';
import { _ } from '/@/locale';
import { updateActiveRecommendationCropConfig } from '/@/store/recommendations/actions';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { Field } from '/@/types/field';
import { daysFromOriginal, flowEventProps, useSeedRecommendationFlow } from '/@/views/recommendations/v2/flow-tracking';
import Section from '/@/views/recommendations/v2/section';
import PhenologyTimeline, { PhenologyLegend } from './phenology-timeline';
import SimulatePlantingDate from './simulate-planting-date';
import { usePhenology } from './use-phenology';

/**
 * "Fenología" tab: stage dates for the chosen planting date, plus a sandbox for trying another
 * one. The simulation is read-only until "Aplicar fecha" — applying it changes the crop config,
 * which invalidates the environment, so the wizard sends the user back to reconfigure it.
 */
function PhenologyTab({
    field,
    crop = 'corn',
    plantingDate,
    compact,
    onPlantingDateApplied,
    onPlantingDateKept,
    onCopy,
    copying,
}: {
    field: Field;
    crop?: 'corn' | 'soy';
    plantingDate?: string;
    compact?: boolean;
    /** Called once the new planting date is stored, to send the user back to the environment step. */
    onPlantingDateApplied: () => void;
    /** Called when the new date was stored without touching the environment or the hybrids. */
    onPlantingDateKept: () => void;
    /** Renders the "copy as image" button on the timeline section when set. */
    onCopy?: () => void;
    copying?: boolean;
}) {
    const dispatch = useDispatch();
    const { hybrids, isLoading } = usePhenology({ field, crop, plantingDate });

    /** The simulated date waiting on the confirmation modal; unset means the modal is closed. */
    const [pendingDate, setPendingDate] = useState<string | undefined>(undefined);

    const flow = useSeedRecommendationFlow();

    /** Both ways a date gets applied report it: through the modal, or straight away when it keeps the environment. */
    const trackApplied = (date: string) => {
        mpTrackEvent('Seed recommendation planting date applied', {
            ...flowEventProps(flow),
            days_from_original: daysFromOriginal(plantingDate, date),
            simulations_count: flow.simulations.current,
        });
        // The next date the user tries is measured against the one just applied.
        flow.simulations.current = 0;
    };

    const applyDate = () => {
        if (!pendingDate) return;
        trackApplied(pendingDate);
        dispatch(updateActiveRecommendationCropConfig({ key: 'plantingDate', value: pendingDate }));
        // The hybrids were chosen for the old environment, so they no longer apply.
        dispatch(updateActiveRecommendationCropConfig({ key: 'products', value: [] }));
        setPendingDate(undefined);
        onPlantingDateApplied();
    };

    /**
     * The date leaves the environment and the hybrids as they are, so there's nothing to warn
     * about or reconfigure: store it and let the wizard carry on.
     */
    const handleApplyRequested = (date: string, keepsEnvironment: boolean) => {
        if (!keepsEnvironment) {
            setPendingDate(date);
            return;
        }
        trackApplied(date);
        dispatch(updateActiveRecommendationCropConfig({ key: 'plantingDate', value: date }));
        onPlantingDateKept();
    };

    /** Cancelar and closing the modal both leave the planting date as it was. */
    const cancelApply = () => {
        mpTrackEvent('Seed recommendation planting date apply canceled', flowEventProps(flow));
        setPendingDate(undefined);
    };

    if (isLoading && !hybrids.length) {
        return (
            <Stack alignItems='center' py={6}>
                <Spinner />
            </Stack>
        );
    }

    return (
        <Stack spacing={3}>
            <Section
                title={_('Fenología según la fecha de siembra')}
                subtitle={_('Fechas de cada estadio y período crítico según la fecha de siembra.')}
                block={onCopy ? 'phenology' : undefined}
                onCopy={onCopy}
                copying={copying}
            >
                <PhenologyTimeline hybrids={hybrids} compact={compact} />
                <PhenologyLegend />
            </Section>

            <SimulatePlantingDate
                field={field}
                crop={crop}
                plantingDate={plantingDate}
                compact={compact}
                onApplyRequested={handleApplyRequested}
            />

            <ModalV2
                open={pendingDate !== undefined}
                onClose={cancelApply}
                title={_('Ajustar fecha de siembra')}
                maxWidth='xs'
                actions={
                    <Stack direction='row' spacing={2} justifyContent='flex-end' width='100%'>
                        <Button variant='text' color='secondary' onClick={cancelApply}>
                            {_('Cancelar')}
                        </Button>
                        <Button variant='contained' color='secondary' onClick={applyDate} data-testid='confirm-date'>
                            {_('Cambiar fecha')}
                        </Button>
                    </Stack>
                }
            >
                <Stack p={2}>
                    <Typography variant='body2' color='primary' textAlign='center'>
                        {_(
                            'Con la nueva fecha de siembra, cambia el ambiente del lote. Vas a tener que volver a configurarlo y elegir los híbridos para obtener una nueva recomendación.'
                        )}
                    </Typography>
                </Stack>
            </ModalV2>
        </Stack>
    );
}

export default PhenologyTab;
